import type {
  CalendarDisplayData,
  CalendarSourceData
} from './calendarStore.types';

type CalendarMapping = Record<string, string[]>;

export const recalculateMappings = (calendars: {
  [calendarId: string]: CalendarDisplayData;
}): {
  entityIdsPerCalendarId: CalendarMapping;
  calendarIdsPerEntityId: CalendarMapping;
} => {
  const entityIdsPerCalendarId = Object.entries(calendars).reduce(
    (acc, [calendarId, calendar]) => {
      acc[calendarId] = getSourceEntityIds(calendar.sources);
      return acc;
    },
    {} as Record<string, string[]>
  );

  const uniqueEntityIds = new Set<string>();
  Object.values(calendars)
    .flatMap((calendar) => getSourceEntityIds(calendar.sources))
    .forEach((entityId) => {
      if (!uniqueEntityIds.has(entityId)) uniqueEntityIds.add(entityId);
    });

  const calendarIdsPerEntityId = [...uniqueEntityIds].reduce(
    (acc, entityId) => {
      acc[entityId] = Object.entries(calendars)
        .filter(([_, cal]) =>
          cal.sources.some(
            (src) => src.planningEntityIdentifier.entityId == entityId
          )
        )
        .map(([calendarId]) => calendarId);
      return acc;
    },
    {} as Record<string, string[]>
  );

  return {
    entityIdsPerCalendarId,
    calendarIdsPerEntityId
  };
};

// Note that zustand does shallow-merge with return value, so no need to include original state in return value.
export const withMappings = (calendars: {
  [calendarId: string]: CalendarDisplayData;
}) => {
  const { calendarIdsPerEntityId, entityIdsPerCalendarId } =
    recalculateMappings(calendars);
  return { calendars, calendarIdsPerEntityId, entityIdsPerCalendarId };
};

export const getSourceEntityIds = (sources: CalendarSourceData[]): string[] =>
  sources.map((src) => src.planningEntityIdentifier.entityId!);

export const getNewCalendarId = (calendars: {
  [calendarId: string]: CalendarDisplayData;
}): string => {
  const calendarIds = Object.keys(calendars).map((id) => Number(id) || 0);
  return (Math.max(0, ...calendarIds) + 1).toString();
};

export const getNextCalendarOrder = (calendars: {
  [calendarId: string]: CalendarDisplayData;
}): number => {
  const calendarOrders = Object.values(calendars).map(
    (calendar) => calendar.order || 0
  );
  return Math.max(0, ...calendarOrders) + 1;
};

export const reorderCalendars = (
  sortedCalendars: CalendarDisplayData[]
): { [calendarId: string]: CalendarDisplayData } => {
  return Object.fromEntries(
    sortedCalendars.map((calendar, index) => [
      calendar.calendarId,
      {
        ...calendar,
        order: index + 1
      }
    ])
  );
};
